import { Breadcrumb, Button, Card, DatePicker, Divider, Select, Skeleton } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import EmptyData from '../../components/common/EmptyData';
import { deleteReportApi, getAdminEmployeeReportings } from '../../services/api_collection';
import { FlexWrapper } from '../../theme/common_style';
import { DropdownIconNew, LmsIcon, ReportNotFoundIcon, TrashIconNew } from '../../theme/SvgIcons';
import {
  actionTypeEnums,
  apiModules,
  ReportingRangeOptions,
  ReportingType
} from '../../utils/constant';
import useProjectOptions from '../../hooks/useProjectOptions';
import { checkPermission, convertToLocalTime } from '../../utils/common_functions';
import Cards from './Cards';
import CreateReport from './CreateReport';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import { StickyBox } from '../../utils/style';
import './ViewReporting.css';
import TableLoader from '../../components/loaders/TableLoader';
import styled from '@emotion/styled';
import CreateManualReport from './CreateManualReport';

const ViewReporting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, reportName = null, startDate = null, endDate = null } = location.state || {};
  const { isEmployee, data: userData } = useSelector((e) => e.userInfo);
  const activeId = isEmployee ? userData?.user_details?.id : id;
  const { options } = useProjectOptions(activeId);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectValue, setSelectValue] = useState(isEmployee ? 'last7days' : null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);
  const RangePicker = DatePicker.RangePicker;
  const currentPageRef = useRef(1);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const loadingRef = useRef(false);

  const { CREATE } = actionTypeEnums;
  const { REPORTING } = apiModules;
  const canCreate = checkPermission(REPORTING, CREATE, userData?.permissions);

  const [projectId, setProjectId] = useState(null);
  const [isCardOpen, setIsCardOpen] = useState(null);

  const [createModal, setCreateModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleGetList = async (nextPage = 1, append = false) => {
    loadingRef.current = true;
    try {
      setLoading(true);
      let params = new URLSearchParams();
      if (limit) params?.append('limit', limit);
      if (nextPage) params?.append('page', nextPage);
      if (isEmployee) {
        params?.append('employeeId', activeId);
      } else {
        activeId && params?.append('employeeId', activeId);
      }
      projectId && params?.append('projectId', projectId);

      params?.append(
        'startDate',
        dayjs(dateRange[0] || startDate || dayjs().subtract(6, 'day').startOf('day')).format(
          'YYYY-MM-DD'
        )
      );
      params?.append(
        'endDate',
        dayjs(dateRange[1] || endDate || dayjs().endOf('day')).format('YYYY-MM-DD')
      );

      const res = await getAdminEmployeeReportings(params);
      if (res?.statusCode === 200) {
        let array = (res?.data?.reportings || [])?.map((el, idx) => ({
          ...el,
          key: (nextPage - 1) * limit + idx + 1
        }));
        if (append) {
          setData((prev) => [...prev, ...array]);
          currentPageRef.current = nextPage;
        } else {
          setData(array || []);
          currentPageRef.current = 1;
          setIsFirstLoad(false);
        }
        setHasMore(array.length === limit);
      } else {
        toast.error(res?.message || 'Something went wrong');
        setHasMore(false);
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
      setHasMore(false);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      let res = await deleteReportApi(createModal?.report?.reporting_id);
      if (res?.statusCode === 200) {
        setCreateModal({ isOpen: false });
        toast.success(res?.message || 'Successfully deleted');
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  const predefinedRanges = () => ({
    selectedDate: [dayjs().startOf('day'), dayjs().endOf('day')],
    today: [dayjs().startOf('day'), dayjs().endOf('day')],
    yesterday: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
    last7days: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    last30days: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')]
  });

  const ranges = predefinedRanges();

  useEffect(() => {
    if (isEmployee && location?.pathname !== '/reporting') {
      navigate('/reporting');
    }
    if (activeId) {
      currentPageRef.current = 1;
      setIsFirstLoad(true);
      handleGetList(1, false);
    } else {
      navigate('/reporting');
    }
  }, [dateRange, projectId]);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container || loadingRef.current || !hasMore) return;
      if (container.scrollHeight - container.scrollTop - container.clientHeight < 100) {
        const nextPage = currentPageRef.current + 1;
        handleGetList(nextPage, true);
      }
    };
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, hasMore, dateRange, projectId]);

  return (
    <div>
      {createModal?.isOpen && !createModal?.isDelete && isEmployee && (
        <CreateReport
          open={createModal?.isOpen}
          onCancel={() => setCreateModal({ isOpen: false })}
          handleList={handleGetList}
          editDetails={createModal?.report}
        />
      )}

      {createModal?.isOpen &&
        !createModal?.isDelete &&
        createModal?.report?.reporting_type === ReportingType.MANUAL && (
          <CreateManualReport
            open={createModal?.isOpen}
            onCancel={() => setCreateModal({ isOpen: false })}
            handleList={handleGetList}
            editDetails={createModal?.report}
          />
        )}

      {createModal?.isOpen && createModal?.isDelete && (
        <ConfirmationModal
          open={createModal?.isOpen}
          onCancel={() => setCreateModal({ isOpen: false })}
          title={'Delete Report'}
          onSubmit={handleDelete}
          buttonName={'Delete'}
          description={'Are you sure you want to delete this Report?'}
          iconBG={'red'}
          icon={<TrashIconNew />}
          loading={deleteLoading}
        />
      )}

      <StickyBox>
        <FlexWrapper justify="space-between" gap="10px">
          {!isEmployee ? (
            <Breadcrumb
              items={[
                { title: <Link to="/reporting">Reporting</Link> },
                ...(isEmployee
                  ? []
                  : [
                      {
                        title: reportName
                      }
                    ])
              ]}
            />
          ) : (
            <>
              {isEmployee && canCreate && (
                <Button
                  type="text"
                  prefixCls="antCustomBtn"
                  onClick={() => setCreateModal({ isOpen: true })}>
                  Add New Report
                </Button>
              )}
            </>
          )}
          <FlexWrapperheading gap="6px">
            <Select
              allowClear
              suffixIcon={<DropdownIconNew />}
              className="select-reporting"
              value={projectId}
              placeholder="Select Project"
              onChange={(val) => {
                setProjectId(val);
                setIsCardOpen(null);
              }}
              options={options}
            />

            {selectValue === 'customRange' && (
              <FlexWrapper
                align="center"
                cursor="default"
                wrap="nowrap"
                gap="4px"
                className="custom-range-report">
                <LmsIcon />
                <RangePicker
                  allowClear
                  format={'YYYY-MM-DD'}
                  suffixIcon={null}
                  onChange={(val) => {
                    if (val && val[0] && val[1]) {
                      const [start, end] = val[0].isBefore(val[1])
                        ? [val[0], val[1]]
                        : [val[1], val[0]];
                      setDateRange([start, end]);
                    } else {
                      setDateRange([null, null]);
                    }
                    setIsCardOpen(null);
                  }}
                  value={[
                    dateRange?.[0]
                      ? dayjs(dateRange?.[0]).isValid()
                        ? dayjs(dateRange?.[0])
                        : null
                      : null,
                    dateRange?.[1]
                      ? dayjs(dateRange?.[1]).isValid()
                        ? dayjs(dateRange?.[1])
                        : null
                      : null
                  ]}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  className="date-range"
                  style={{
                    width: '100%',
                    border: 'none',
                    boxShadow: 'none',
                    outline: 'none',
                    height: '40px',
                    borderRadius: '8px',
                    textAlign: 'center !important'
                  }}
                />
              </FlexWrapper>
            )}

            <Select
              allowClear={isEmployee ? false : true}
              suffixIcon={<DropdownIconNew />}
              className="select-reporting"
              value={selectValue}
              placeholder="Select Range"
              onChange={(val) => {
                setSelectValue(val);
                if (!val) {
                  setDateRange([null, null]);
                  setSelectValue(null);
                } else if (val === 'customRange') {
                  return;
                } else {
                  setDateRange(ranges[val]);
                }
              }}
              options={ReportingRangeOptions}
            />
          </FlexWrapperheading>
        </FlexWrapper>
      </StickyBox>
      {isFirstLoad && loading ? (
        <Card style={{ margin: '20px 0' }}>
          <FlexWrapper justify="space-between" gap="50px" width="100%" wrap="nowrap">
            <FlexWrapper direction="column" gap="10px" justify="left" align="start" width="100%">
              <Skeleton.Input active style={{ width: '300px', height: '30px' }} />
              <Skeleton.Input active style={{ width: '150px', height: '20px' }} />
              <Divider style={{ borderColor: '#F1F1F1', borderWidth: '1.5px', margin: '0px' }} />
              <Skeleton active size="large" paragraph={{ rows: 4 }} style={{ width: '80%' }} />
            </FlexWrapper>
            <Skeleton.Node active prefixCls="custom-reporting-skeleton" />
          </FlexWrapper>
        </Card>
      ) : data?.length > 0 ? (
        <div
          ref={scrollContainerRef}
          className="scrollable-report-list"
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '16px 8px',
            borderRadius: '8px'
          }}>
          <FlexWrapper gap="20px" direction="column">
            {data?.map((report, idx) => (
              <Cards
                report={report}
                key={idx}
                empName={report?.project?.name}
                date={convertToLocalTime(report?.updated_at)}
                isCardOpen={isCardOpen}
                setIsCardOpen={setIsCardOpen}
                handleReport={setCreateModal}
              />
            ))}
            {loading && <TableLoader size="15px" />}
          </FlexWrapper>
        </div>
      ) : (
        <EmptyData
          height={'70vh'}
          icon={<ReportNotFoundIcon />}
          title={'No Report'}
          subTitle={'No Report Found'}
        />
      )}
    </div>
  );
};

export default ViewReporting;
const FlexWrapperheading = styled.div`
  width: ${(props) => props.width};
  display: flex;
  flex-wrap: ${(props) => props.wrap || 'wrap'};
  flex-direction: ${(props) => props.direction || 'row'};
  justify-content: ${(props) => props.justify || 'center'};
  align-items: ${(props) => props.align || 'center'};
  gap: ${(props) => props.gap || '10px'};
  cursor: ${(props) => props.cursor || 'auto'};
  margin: ${(props) => props.margin || '0px'};
  .select-reporting {
    width: 150px;
    height: 40px;
  }
  .custom-range-report {
    background-color: white;
    padding: 0 0 0 12px;
    border-radius: 8px;
    border: 1px solid #e4e4e4;
  }
  @media (max-width: 768px) {
    width: 100%;
    align-items: start;
    justify-content: start;
    .select-reporting {
      width: 100%;
      height: 40px;
    }
    .custom-range-report {
      width: 100%;
    }
  }
  .select-reporting {
    width: 150px;
    height: 40px;
    @media (max-width: 680px) {
      width: 100%;
    }
  }
`;
